import {
  EntityOperation,
  logEntityOperationFailure,
} from 'serviceWorker/helpers/entityFailureLog';
import type { PendingPublish } from './pendingPublish';
import { Services } from 'shared/domain/messages/message';
import type { PullingContract } from 'serviceWorker/helpers/broadcastPullError';
import { ServiceData } from 'serviceWorker/db/service';
import { debugLog } from 'shared/logger/debugLog';
import { retry } from 'shared/utils/retry';
import { MAX_CONCURENT_PULLS, MAX_CONCURENT_PULL_PAGE_SIZE } from './concurentPullConstants';
import { handlePullFailure } from './handlePullFailure';
import { captureCollectionScope } from 'serviceWorker/helpers/dataScope';
import {
  FailedPull,
  HeadersData,
  NormalizedPagedEntityResponseData,
  Pull,
} from './types';


interface MakePullPagedEntityProps {
  fetchPaged: (
    data: HeadersData,
    abortController: AbortController,
    offset: number,
    size: number
  ) => Promise<any> | undefined;
  channelContract: PullingContract;
  entityRepository: any;
  entityService: any;
  entityName: Services;
  finishedServiceStateFactory: (
    total: number,
    syncKey: string
  ) => Partial<ServiceData>;
  /** The collection event asked for by syncs, published once the download is stored. */
  pendingCollection: PendingPublish;
}
async function processFetch(
  fetchPaged,
  entityService: any,
  entityRepository: any,
  data: any,
  abortController: any,
  page: { offset: number; size: number },
  activeFetches: any[]
): Promise<any> {
  activeFetches.push(true);
  try {
    const response = await retry(async () => {
      const res = await fetchPaged(
        data,
        abortController,
        page.offset,
        page.size
      );
      if (!res) {
        throw new Error('No response.');
      }
      return res;
    }, 2);
    activeFetches.pop();
    if (abortController.signal.aborted) {
      return;
    }
    await entityService.add({
      isDownloading: true,
    });

    if (abortController.signal.aborted) {
      return;
    }
    const start = Date.now();
    await entityRepository.addBatch(response.items);
    const end = Date.now();
    debugLog(
      `entityRepository paged insert of ${response.items.length} items time:`,
      end - start
    );
  } catch (e) {
    activeFetches.pop();
    throw e;
  }
}

function processFetches(
  fetchPaged,
  entityService,
  entityRepository,
  data: any,
  abortController: AbortController,
  fetchesToDo: { offset: number; size: number }[],
  activeFetches: any[],
  resolve: (value?: unknown) => void,
  reject: (error: Error) => void
): void {
  if (abortController.signal.aborted) {
    return reject(new Error('Aborted'));
  }

  if (!fetchesToDo.length && !activeFetches.length) {
    return resolve();
  }

  while (
    fetchesToDo.length &&
    activeFetches.length < MAX_CONCURENT_PULLS
  ) {
    const page = fetchesToDo.pop()!;
    processFetch(
      fetchPaged,
      entityService,
      entityRepository,
      data,
      abortController,
      page,
      activeFetches
    )
      .then(() => {
        processFetches(
          fetchPaged,
          entityService,
          entityRepository,
          data,
          abortController,
          fetchesToDo,
          activeFetches,
          resolve,
          reject
        );
      })
      .catch((e) => reject(e));
  }
}

export function makePullPagedEntity({
  fetchPaged,
  channelContract,
  entityRepository: storedEntities,
  entityService: storedService,
  entityName,
  finishedServiceStateFactory,
  pendingCollection,
}: MakePullPagedEntityProps): Pull {
  return async (data, abortController, callerScope): Promise<void | FailedPull> => {
    // Every write of the pull goes through its scope: none reaches data that became outdated meanwhile.
    const scope = callerScope ?? captureCollectionScope(storedService, data.projectId, entityName);
    const entityService = {
      add: scope.guard(storedService.add),
      stopDownloading: scope.guard(storedService.stopDownloading),
    };
    const countStoredEntities: () => Promise<number> = storedEntities.quantity;
    const entityRepository = {
      get: scope.guard(storedEntities.get),
      clear: scope.guard(storedEntities.clear),
      quantity: scope.guard(countStoredEntities),
      addBatch: scope.guard(storedEntities.addBatch),
    };
    const publishCollection = (): Promise<void | undefined> => scope.write(async () => pendingCollection.flush());
    const stopDownloadingOnly = async (): Promise<void> => {
      await entityService.stopDownloading();
    };
    const firstPageParams = {
      size: 100,
      offset: 0,
    };
    const pagesAmount = 1;

    let promise: Promise<NormalizedPagedEntityResponseData> | undefined;
    try {
      promise = fetchPaged(
        data,
        abortController,
        firstPageParams.offset,
        firstPageParams.size
      );
    } catch (e: unknown) {
      logEntityOperationFailure(EntityOperation.FETCHING, {
        entityName,
        projectId: data.projectId,
        error: e,
      });
      await stopDownloadingOnly();
      return;
    }

    if (!promise) {
      await stopDownloadingOnly();
      return;
    }

    await entityService.add({
      total: undefined,
      isDownloading: true,
      isDownloaded: false,
      syncKey: undefined,
    });
    return promise
      .then(async (res: NormalizedPagedEntityResponseData) => {
        if (abortController.signal.aborted) {
          return;
        }
        if (!res) {
          await entityService.add({
            isDownloading: false,
          });
          return;
        }
        await entityService.add({
          total: res.items.length,
          isDownloading: true,
        });

        if (abortController.signal.aborted) {
          return;
        }
        await entityRepository.addBatch(res.items);
        const syncKey = res.syncKey;

        const total = await entityRepository.quantity();
        if (res.hasMore && (total ?? 0) < res.totalCount) {
          const estimatedPulls = Math.max(
            1,
            Math.floor(
              (res.totalCount - firstPageParams.size) /
                MAX_CONCURENT_PULL_PAGE_SIZE
            ) + 1
          );

          await pullPagesConcurrentlyWithLimit(
            fetchPaged,
            entityRepository,
            entityService,
            publishCollection,
            finishedServiceStateFactory,
            data,
            abortController,
            {
              ...firstPageParams,
              offset:
                firstPageParams.offset +
                firstPageParams.size * pagesAmount,
              size: MAX_CONCURENT_PULL_PAGE_SIZE,
              firstSyncKey: syncKey,
              pagesAmount: estimatedPulls,
            }
          );
        } else {
          const entityServiceData = finishedServiceStateFactory(
            res.items.length,
            syncKey
          );

          // for some reason getting all data makes IndexedDB faster next time
          const start = Date.now();
          debugLog(`start ${entityName} getAll:`, start);
          await entityRepository.get();
          await entityService.add(entityServiceData);
          const end = Date.now();
          debugLog(`entityRepository ${entityName} time:`, end - start);
          await publishCollection();
        }
      })
      .catch((error: unknown) =>
        handlePullFailure({
          error,
          abortController,
          entityName,
          projectId: data.projectId,
          channelContract,
          scope,
          stopDownloading: stopDownloadingOnly,
          resetStoredData: async () => {
            await entityRepository.clear();
            await entityService.add({
              total: undefined,
              isDownloading: false,
              isDownloaded: false,
              syncKey: undefined,
            });
          },
        })
      );
  };
}

interface PagingProps {
  offset: number;
  size: number;
  firstSyncKey: string;
  pagesAmount?: number;
}

async function pullPagesConcurrentlyWithLimit(
  fetchPaged: any,
  entityRepository: any,
  entityService: any,
  publishCollection: () => Promise<unknown>,
  createFinishedEntityServiceData: (
    count: number,
    firstSyncKey: string
  ) => any,
  data: any,
  abortController: AbortController,
  props: PagingProps
): Promise<void> {
  const activeFetches: any[] = [];

  const firstCallSyncKey = props.firstSyncKey;
  const params = {
    offset: props.offset,
    size: props.size,
  };

  const fetchesToDo = Array.from({ length: props.pagesAmount || 1 })
    .map((_, multiplier) => {
      return {
        offset: params.offset + multiplier * params.size,
        size: params.size,
      };
    })
    .reverse();

  await new Promise((resolve, reject) => {
    processFetches(
      fetchPaged,
      entityService,
      entityRepository,
      data,
      abortController,
      fetchesToDo,
      activeFetches,
      resolve,
      reject
    );
  });

  const start = Date.now();
  await entityRepository.get();
  const end = Date.now();
  debugLog('pullPagesConcurrentlyWithLimit getAll time: ', end - start);
  const count = await entityRepository.quantity();
  if (abortController.signal.aborted) {
    return;
  }
  const finishedEntityServiceData = createFinishedEntityServiceData(
    count,
    firstCallSyncKey
  );
  await entityService.add(finishedEntityServiceData);
  await publishCollection();
}
